import React, { useRef } from 'react'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import styled from 'styled-components'
import { CreationToolFrame, CreationToolSourceFile } from '../types'
import CreationToolGradient from '../types/gradient'
import getSourceFrames from '../utils/get-source-frames'

const CANVAS_SCALE = 3 // this is used to prevent scaling down images too far (looks blurry)

const Container = styled.div`
    background: #000;
    border-radius: inherit;
    overflow: hidden;
    position: relative;
`

const Canvas = styled.canvas`
    height: 100%;
    left: 0;
    position: relative;
    top: 0;
    width: 100%;
`

type Props = {
    className?: string
    source: CreationToolSourceFile
}

const CreationToolsFrameStrip = ({ className, source }: Props) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useAsyncEffect(async () => {
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')

        if (!canvas || !context) return

        const { height, width } = canvas.getBoundingClientRect()

        canvas.width = width * CANVAS_SCALE
        canvas.height = height * CANVAS_SCALE

        const aspectRatio = source.width / source.height
        const frameWidth = canvas.height * aspectRatio
        const frameCount = Math.ceil(canvas.width / frameWidth)
        let frames: CreationToolFrame[] = []

        try {
            frames = await getSourceFrames({ sourceFile: source, length: frameCount })
        } catch (err) {}

        // if no frames, draw placeholders
        if (!frames.length) {
            const colors = CreationToolGradient.colors(CreationToolGradient.BlackGrey)
            const gradient = context.createLinearGradient(0, 0, frameWidth, 0)
            gradient.addColorStop(0, colors[0])
            gradient.addColorStop(1, colors[1])
            context.fillStyle = gradient

            for (let i = 0; i < frameCount; i++) {
                context.fillRect(0, 0, frameWidth, canvas.height)
                context.translate(frameWidth, 0)
            }
        } else {
            frames.forEach((frame, i) => {
                if (frame?.canvas) {
                    context.drawImage(frame.canvas, i * frameWidth, 0, frameWidth, canvas.height)
                }
            })
        }
    }, [canvasRef, source])

    return (
        <Container className={className}>
            <Canvas ref={canvasRef} />
        </Container>
    )
}

export default CreationToolsFrameStrip
